"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import "../styles/mobile-verification-modal.css"
import { webAuth } from "../utils/auth0" // Import webAuth for Auth0 APIs
import CountrySelectReact from "../utils/CountrySelect"
import { getDialCodeFromUserProfile } from "../utils/localeDetection"
import phoneExamples from "../utils/phoneExamples.json"

export default function MobileVerificationModal({
  open = false,
  onClose = () => {},
  onVerified = () => {},
  onLogout = () => {},
  initialCountryCode = "+91",
  initialPhone = "",
  redirectSeconds = 5,
  className = "",
  isClosable = true,
}) {
  const overlayRef = useRef(null)
  const phoneInputRef = useRef(null)
  const otpRef0 = useRef(null)
  const otpRef1 = useRef(null)
  const otpRef2 = useRef(null)
  const otpRef3 = useRef(null)
  const otpRef4 = useRef(null)
  const otpRef5 = useRef(null)
  const otpRefs = [otpRef0, otpRef1, otpRef2, otpRef3, otpRef4, otpRef5]

  const userData = useSelector((state) => state.user?.data)
  const defaultCountryCode = useMemo(
    () => getDialCodeFromUserProfile(userData) || initialCountryCode,
    [userData, initialCountryCode]
  )

  const [step, setStep] = useState("phone") // "phone" | "otp" | "success"
  const [countryCode, setCountryCode] = useState(defaultCountryCode)
  const [phone, setPhone] = useState(initialPhone)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState("")

  const [otp, setOtp] = useState(["", "", "", "", "", ""])
  const [verifying, setVerifying] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)
  const [countdown, setCountdown] = useState(redirectSeconds)
  const [is409Error, setIs409Error] = useState(false)

  // Format phone number with country code
  const formatPhoneNumber = (phoneNum, code = countryCode) => {
    // Remove all non-digit characters
    const cleanPhone = phoneNum.replace(/\D/g, "")
    
    // If already starts with +, return as is
    if (phoneNum.startsWith("+")) {
      return phoneNum
    }
    
    // Default: just append country code
    return code + cleanPhone
  }

  // Validate E164 format
  const isValidE164 = (v) => {
    if (!v || typeof v !== "string") return false
    return /^\+[0-9]{10,15}$/.test(v.trim())
  }

  const fullPhone = useMemo(() => formatPhoneNumber(phone), [phone, countryCode])

  const phonePlaceholder = useMemo(
    () => phoneExamples[countryCode] || "9759392985",
    [countryCode]
  )

  const maskedPhone = useMemo(() => {
    const digits = phone.replace(/\D/g, "")
    if (!digits) return "XXXXXXXXXX"
    const last4 = digits.slice(-4)
    return `XXXXXX${last4}`
  }, [phone])

  // Lock scroll and reset state on open/close
  useEffect(() => {
    if (open) {
      document.body.classList.add("mv-no-scroll")
      setStep("phone")
      setOtp(["", "", "", "", "", ""])
      setResendTimer(0)
      setCountdown(redirectSeconds)
      setError("")
      setIs409Error(false)
      
      // Check for 409 conflict error from previous attempt
      const phoneLinkError = localStorage.getItem('phone_link_error');
      
      if (phoneLinkError) {
        // This is a 409 conflict case - display user-friendly message
        setIs409Error(true);
        setError(phoneLinkError);
        // Clear the error after displaying (one-time display)
        localStorage.removeItem('phone_link_error');
      }
      
      setTimeout(() => phoneInputRef.current && phoneInputRef.current.focus(), 60)
    } else {
      document.body.classList.remove("mv-no-scroll")
    }
    return () => document.body.classList.remove("mv-no-scroll")
  }, [open, redirectSeconds])

  useEffect(() => {
    if (open) {
      setCountryCode(defaultCountryCode)
    }
  }, [open, defaultCountryCode])

  // ESC to close (only if closable)
  useEffect(() => {
    if (!open) return
    if (!isClosable) return
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose, isClosable])

  // Resend cooldown
  useEffect(() => {
    if (resendTimer <= 0) return
    const t = setInterval(() => setResendTimer((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [resendTimer])

  // Success countdown
  useEffect(() => {
    if (step !== "success") return
    if (countdown <= 0) {
      onVerified(fullPhone)
      onClose()
      return
    }
    const t = setInterval(() => setCountdown((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [step, countdown, onVerified, onClose, fullPhone])

  const isValidPhone = () => {
    const digits = phone.replace(/\D/g, "")
    return digits.length >= 8 && digits.length <= 14
  }

  const handleContinue = async () => {
    if (!isValidPhone()) return
    setIsSending(true)
    setError("")
    
    const formattedPhone = formatPhoneNumber(phone, countryCode)
    
    if (!isValidE164(formattedPhone)) {
      setError("Please enter a valid phone number")
      setIsSending(false)
      return
    }
    
    // Save the current access token before starting OTP flow (needed for identity linking later)
    // This is same as EmailPhoneLink.js - save before passwordless flow
    const currentToken = localStorage.getItem("access_token")
    if (currentToken) {
      localStorage.setItem("access_id", currentToken)
    }
    
    // Set linkaccount flag BEFORE starting OTP flow (same as EmailPhoneLink.js)
    // This ensures CallbackPage sees the flag when Auth0 redirects after passwordlessLogin
    localStorage.setItem("linkaccount", "true")
    localStorage.setItem("linkaccount_time", Date.now().toString())
    
    // Use Auth0 passwordlessStart to send OTP
    webAuth.passwordlessStart(
      {
        connection: "sms",
        phoneNumber: formattedPhone,
        send: "code",
      },
      (err) => {
        setIsSending(false)
        if (err) {
          setError(err.description || "Failed to send SMS")
        } else {
          setStep("otp")
          setResendTimer(30)
          setTimeout(() => otpRefs[0].current && otpRefs[0].current.focus(), 60)
        }
      }
    )
  }

  const handleResend = async () => {
    if (resendTimer > 0) return
    setError("")
    
    // Clear OTP input field when resending
    setOtp(["", "", "", "", "", ""])
    
    const formattedPhone = formatPhoneNumber(phone, countryCode)
    
    if (!isValidE164(formattedPhone)) {
      setError("Please enter a valid phone number")
      return
    }
    
    // Use Auth0 passwordlessStart to resend OTP
    webAuth.passwordlessStart(
      {
        connection: "sms",
        phoneNumber: formattedPhone,
        send: "code",
      },
      (err) => {
        if (err) {
          setError(err.description || "Failed to resend SMS")
        } else {
          setResendTimer(30)
          // Focus on first OTP input field after clearing
          setTimeout(() => otpRefs[0].current && otpRefs[0].current.focus(), 60)
        }
      }
    )
  }

  const handleVerify = async () => {
    const code = otp.join("")
    if (code.length !== 6 || /\D/.test(code)) return
    setVerifying(true)
    setError("")
    
    const formattedPhone = formatPhoneNumber(phone, countryCode)
    
    // Use Auth0 passwordlessLogin to verify OTP
    webAuth.passwordlessLogin(
      {
        connection: "sms",
        phoneNumber: formattedPhone,
        verificationCode: code,
      },
      (err, authResult) => {
        setVerifying(false)
        if (err) {
          setError(err.description || "OTP verification failed")
        } else {
          // Store the access token if provided (same as EmailPhoneLink.js)
          if (authResult && authResult.accessToken) {
            localStorage.setItem("access_token", authResult.accessToken)
          }
          
          // Note: linkaccount flag was already set in handleContinue before sending OTP
          // CallbackPage will handle identity linking when Auth0 redirects after passwordlessLogin
          // Don't call onVerified here - wait for CallbackPage to complete add-identity API
          // If add-identity succeeds, CallbackPage will navigate to Dashboard with success
          // If add-identity fails with 409, CallbackPage will navigate with error flag
          
          // Just show success step and let user wait for redirect
          setStep("success")
          setCountdown(redirectSeconds)
        }
      }
    )
  }

  const updateOtpAt = (index, value) => {
    const v = value.replace(/\D/g, "").slice(-1)
    setOtp((prev) => {
      const next = [...prev]
      next[index] = v
      return next
    })
  }

  const handleOtpChange = (index, e) => {
    const v = e.target.value
    const wasEmpty = otp[index] === ""
    updateOtpAt(index, v)
    if (/\d/.test(v) && wasEmpty) {
      const nextRef = otpRefs[index + 1]?.current
      if (nextRef) {
        nextRef.focus()
        nextRef.select()
      }
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && otp[index] === "") {
      const prevRef = otpRefs[index - 1]?.current
      prevRef && prevRef.focus()
    }
    if (e.key === "ArrowLeft") otpRefs[index - 1]?.current?.focus()
    if (e.key === "ArrowRight") otpRefs[index + 1]?.current?.focus()
  }

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6)
    if (!text) return
    e.preventDefault()
    const parts = text.split("")
    setOtp((prev) => {
      const next = [...prev]
      for (let i = 0; i < 6; i++) next[i] = parts[i] || ""
      return next
    })
    setTimeout(() => {
      const ref = otpRefs[Math.min(text.length, 5)]?.current
      ref && ref.focus()
    }, 0)
  }

  if (!open) return null

  console.log('MobileVerificationModal: Rendering with open=', open, 'isClosable=', isClosable)

  return (
    <div
      className={`mv-overlay ${isClosable ? '' : 'mv-overlay-locked'}`}
      ref={overlayRef}
      aria-hidden={!open}
      style={{ position: 'fixed', inset: 0, zIndex: 2147483647, pointerEvents: 'all' }}
      onMouseDown={(e) => isClosable && e.target === overlayRef.current && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mv-title"
        className={`mv-modal ${className}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mv-modal-header">
          <div className="mv-header-left">
            <button
              type="button"
              className="mv-logout-btn"
              onClick={onLogout}
              aria-label="Logout"
            >
              Logout
            </button>
          </div>
          <button
            className={`mv-close ${!isClosable ? "mv-close-disabled" : ""}`}
            aria-label="Close"
            onClick={() => isClosable && onClose()}
            title={isClosable ? "Close" : "Verification required"}
          >
            <span className="mv-close-x" />
          </button>
        </div>

        {/* Illustration */}
        <div className="mv-illustration" aria-hidden="true">
          <div className="mv-illustration-inner">
            <div className="mv-phone-icon">
              <div className="mv-phone-notch" />
              <div className="mv-phone-dot" />
            </div>
          </div>
        </div>

        {step === "phone" && (
          <>
            <h3 id="mv-title" className="mv-title">
              Verify Your Number
            </h3>
            <p className="mv-subtitle">Enter your mobile number to receive an OTP.</p>

            <div className="mv-input-row" aria-live="polite">
              {error && (
                <div className="mv-error-message" role="alert">
                  {is409Error
                    ? "This number is already linked. Please try with another number."
                    : error}
                </div>
              )}
              <div className="mv-country-select">
                <CountrySelectReact
                  value={countryCode}
                  onChange={setCountryCode}
                  maxWidth={110}
                  variant="underline"
                />
              </div>

              <input
                ref={phoneInputRef}
                className="mv-phone-input"
                type="tel"
                inputMode="numeric"
                placeholder={phonePlaceholder}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s-]/g, ""))}
                aria-label="Mobile number"
              />
            </div>

            <button
              type="button"
              className={`mv-primary-btn ${!isValidPhone() || isSending ? "disabled" : ""}`}
              onClick={handleContinue}
              disabled={!isValidPhone() || isSending}
            >
              {isSending ? "Sending..." : "Continue"}
            </button>

            <button
              type="button"
              className="mv-secondary-link"
              onClick={onClose}
              style={{ display: isClosable ? 'inline-block' : 'none' }}
            >
              NO, Other Time
            </button>
          </>
        )}

        {step === "otp" && (
          <>
            <button
              type="button"
              className="mv-back-link mv-back-link-inline"
              onClick={() => setStep("phone")}
              aria-label="Back"
            >
              <span className="mv-back-caret" /> Back
            </button>

            <h3 id="mv-title" className="mv-title">
              Please Enter OTP
            </h3>
            <p className="mv-subtitle">
              We sent a verification code to {countryCode} {maskedPhone}
            </p>

            <div className="mv-otp-row-wrapper">
              {error && (
                <div className="mv-error-message" role="alert">
                  {error}
                </div>
              )}
              <div className="mv-otp-row">
                {otp.map((val, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  className="mv-otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={val}
                  onChange={(e) => handleOtpChange(i, e)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
              </div>
            </div>

            <button
              type="button"
              className={`mv-primary-btn ${verifying || otp.join("").length !== 6 ? "disabled" : ""}`}
              disabled={verifying || otp.join("").length !== 6}
              onClick={handleVerify}
            >
              {verifying ? "Verifying..." : "Verify"}
            </button>

            <div className="mv-hint">
              Didn’t receive the OTP?{" "}
              <button
                type="button"
                className={`mv-inline-link ${resendTimer > 0 ? "disabled" : ""}`}
                onClick={handleResend}
                disabled={resendTimer > 0}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <h3 id="mv-title" className="mv-title">
              Verification Successful!!
            </h3>
            <p className="mv-subtitle">Mobile Number Successfully Verified.</p>

            <button
              type="button"
              className="mv-primary-btn"
              onClick={() => {
                onVerified(fullPhone)
                onClose()
              }}
            >
              Back to Dashboard
            </button>

            <div className="mv-redirect-note">Redirect to dashboard in {countdown} sec.</div>
          </>
        )}
      </div>
    </div>
  )
}
